<template>
  <teleport
    :disabled="!teleport"
    to="body"
  >
    <transition
      v-if="!noContent"
      :name="transitionName"
      @after-enter="handleStateChange(true)"
      @after-leave="handleStateChange(false)"
    >
      <div
        v-show="isActive"
        v-cloak
        ref="overlay"
        class="overlay critical-hide"
        :class="[
          $blockClass(blockClass),
          responsiveType,
          `${responsiveType}--${responsiveDirection}`,
          customClass
        ]"
      >
        <div
          v-if="template"
          ref="scrollElement"
          class="overlay__container"
          :class="[
            $blockClass(blockClass, 'container'),
            $blockClass(responsiveType, 'container'),
            stateClass,
            {'custom-scroll': customScrollActive}
          ]"
          :style="heightOverrideStyles"
        >
          <div
            class="overlay__header"
            :class="[
              $blockClass(blockClass, 'header'),
              $blockClass(responsiveType, 'header'),
            ]"
          >
            <p
              v-if="$slots.title"
              class="overlay__title text-heading-l"
              :class="[
                $blockClass(blockClass, 'title'),
                $blockClass(responsiveType, 'title'),
              ]"
            >
              <slot name="title" />
            </p>

            <button
              class="overlay__close icon-button"
              :class="[
                $blockClass(blockClass, 'close'),
                $blockClass(responsiveType, 'close'),
              ]"
              type="button"
              @click.prevent="closeOverlay(namespace)"
            >
              <span
                class="visually-hidden"
                v-text="$string('accessibility.close')"
              />

              <slot name="close">
                <icon-close />
              </slot>
            </button>
          </div>

          <div
            ref="body"
            class="overlay__body"
            :class="[
              $blockClass(blockClass, 'body'),
              $blockClass(responsiveType, 'body'),
              {
                'overlay__body--no-margin': !$slots.title,
                'has-scroll': customScrollActive
              }
            ]"
          >
            <div
              class="overlay__content"
              :class="[
                $blockClass(blockClass, 'content'),
                $blockClass(responsiveType, 'content'),
              ]"
            >
              <slot
                :is-active="isActive"
                name="body"
              />
            </div>

            <div
              v-if="customScrollActive"
              class="overlay__scrollbar"
              :style="{'height': `${visibleHeight}px`}"
            >
              <div class="overlay__scrollbar-track" />
              <div
                ref="scrollbarThumb"
                class="overlay__scrollbar-thumb"
                :style="{
                  transform: `translateY(${thumbPosition})`,
                  height: `${thumbHeight}%`
                }"
              />
            </div>
          </div>
        </div>

        <!-- If template prop is false then just render body slot -->
        <div
          v-else
          ref="scrollElement"
          class="overlay__container"
          :class="[
            $blockClass(blockClass, 'container'),
            $blockClass(responsiveType, 'container'),
          ]"
        >
          <slot
            :is-active="isActive"
            name="body"
          />
        </div>
      </div>
    </transition>

    <transition name="fade">
      <button
        v-if="isActive && showOverlay"
        class="window-overlay"
        :class="namespace"
        tabindex="-1"
        type="button"
        aria-hidden="true"
        @click.prevent="closeOverlay(namespace)"
      />
    </transition>
  </teleport>
</template>

<script>

/**
 * Vue: Overlay
 * -----------------------------------------------------------------------------
 * Base template for overlays (drawers and modals).
 * - Add `js-overlay="scrollElement"` to element in <slot> to pass that as the
 *   element to disableBodyScroll instead of `.overlay__container`.
 *
 * @param {Boolean} [autoQueue] - Automatically opened overlays are queued.
 * @param {Number} [autoShow] - Time in ms to wait before auto-showing overlay.
 * @param {String} [blockClass] - Block level class to apply to all elements.
 * @param {String} customClass - Addition class independent of blockClass.
 * @param {Boolean} customScroll - Boolean to enable custom scrollbar.
 * @param {String} [stateClass] - Optional state classes for top level element.
 * @param {String} direction - Open animation direction set using transition
 * styles, `up` or `down` for `modal` and `left` or `right` for `drawer`,
 * supports a combination in the format [mobile direction]-[desktop direction].
 * @param {Boolean} [disableScroll] - Trigger `disableBodyScroll` on open.
 * @param {Boolean, Number} heightOverride - Used only for temporary height
 * override. Permanent height changes can be done via css.
 * @param {String} namespace - Unique namespace for overlay, reference when
 * opening/closing the overlay.
 * @param {Boolean} [noContent] - Displays no content, only window overlay, you
 * must manually disable scroll and trap focus in the component which triggers
 * this overlay.
 * @param {Boolean} [showOverlay] - Show the window overlay?
 * @param {Boolean} [teleport] - Teleport window overlay to end of <body>.
 * @param {Boolean} [template] - Render with default templating.
 * @param {String} type - Type of overlay, `modal`, `drawer`, or `shelf`,
 * supports a combination in the format [mobile type]-[desktop type].
 *
 */
import { clearAllBodyScrollLocks, disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import IconClose from '~icons/misc/close.svg'

export default {
  // eslint-disable-next-line vue/multi-word-component-names
  name: 'Overlay',

  components: {
    IconClose,
  },

  props: {
    autoQueue: {
      type: Boolean,
      default: true,
    },
    autoShow: {
      type: Number,
      default: 0,
    },
    blockClass: {
      type: String,
      default: '',
    },
    customClass: {
      type: String,
      default: '',
    },
    customScroll: {
      type: Boolean,
      default: false,
    },
    direction: {
      type: String,
      default: 'up',
      required: true,
      validator: (value) => {
        return value.split('-').every((part) => ['up', 'down', 'left', 'right'].includes(part))
      },
    },
    disableScroll: {
      type: Boolean,
      default: true,
    },
    heightOverride: {
      type: [Boolean, Number],
      default: false,
    },
    namespace: {
      type: String,
      default: '',
      required: true,
    },
    noContent: {
      type: Boolean,
      default: false,
    },
    showOverlay: {
      type: Boolean,
      default: true,
    },
    stateClass: {
      type: String,
      default: '',
    },
    teleport: {
      type: Boolean,
      default: true,
    },
    template: {
      type: Boolean,
      default: true,
    },
    type: {
      type: String,
      default: 'modal',
      required: true,
      validator: (value) => {
        return value.split('-').every((part) => ['drawer', 'modal', 'shelf'].includes(part))
      },
    },
  },

  emits: {
    close: (value) => typeof value === 'string',
    open: (value) => typeof value === 'string',
    'state-change': (value) => typeof value === 'object',
  },

  data() {
    return {
      cnvsGlobal: true,
      scrollElement: false,
      hasOverflow: false,
      totalHeight: 0,
      visibleHeight: 0,
      thumbPosition: 0,
      thumbHeight: 0,
      originalHeight: false,
      newHeight: false,
      mouseMoveHandler: null,
      mouseUpHandler: null,
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getFirstElement: 'accessibility/getFirstElement',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      hydrated: (state) => state.index.hydrated,
      isMobile: (state) => state.index.screen.mobile,
      previousElement: (state) => state.accessibility.previousElement,
    }),

    /**
     * Compute overlay active state.
     * @returns {Boolean}
     */
    isActive() {
      return this.getActiveOverlay === this.namespace
    },

    /**
     * Compute responsive direction based on mobile.
     * @retursn {String}
     */
    responsiveDirection() {
      if (!this.direction.includes('-')) {
        return this.direction
      }

      return this.isMobile
        ? this.direction.split('-')[0]
        : this.direction.split('-')[1]
    },

    /**
     * Compute responsive type based on mobile.
     * @returns {String}
     */
    responsiveType() {
      if (!this.type.includes('-')) {
        return this.type
      }

      return this.isMobile ? this.type.split('-')[0] : this.type.split('-')[1]
    },

    /**
     * Compute transition name based on type.
     * @returns {String}
     */
    transitionName() {
      const type = this.responsiveType === 'modal' ? 'fade' : 'slide'
      return `${type}-${this.responsiveDirection}`
    },

    /**
     * Compute if custom scroll bar is active.
     * @returns {Boolean}
     */
    customScrollActive() {
      return this.customScroll && this.hasOverflow && !this.isMobile
    },

    /**
     * Compute styles for overriding height
     * @returns {Object}
     */
    heightOverrideStyles() {
      if (!this.newHeight) {
        return null
      }

      return { height: `${this.newHeight}px` }
    },
  },

  watch: {

    /**
     * Watch hydrated state to start auto-show countdown.
     */
    hydrated() {
      this.setAutoShow()
    },

    /**
     * Watch active state.
     * - Separate logic for event binding.
     */
    isActive() {
      if (this.isActive) {
        this.$emit('open', this.namespace)
      } else {
        this.$emit('close', this.namespace)
      }

      if (!this.disableScroll || this.noContent) {
        return
      }

      /**
       * If active then:
       * - Set previous element (to focus on close).
       * - Disable body scroll.
       * - Reset scroll element scroll position.
       * - Trap focus inside overlay element.
       * - Focus first trap focus element.
       */
      if (this.isActive) {
        this.getScrollElement()
        this.setPreviousElement(document.activeElement)
        disableBodyScroll(this.scrollElement, this.bodyScrollOptions())

        nextTick().then(() => {
          if (this.scrollElement) {
            this.scrollElement.scrollTo(0, 0)
          }

          this.updateFocusTrap()
        })

        return
      }

      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement)
      } else {
        clearAllBodyScrollLocks()
      }

      /**
       * If no other active overlay when closing then clear focus trap.
       */
      if (!this.getActiveOverlay) {
        this.clearFocusTrap()
      }

      /**
       * Set focus back to previous element if set.
       */
      if (!this.previousElement || typeof this.previousElement !== 'object') {
        return
      }

      this.previousElement.focus()
      this.setPreviousElement()
    },

    /**
     * Determine if content has overflow.
     */
    hasOverflow(state) {
      if (!state) {
        return
      }

      nextTick().then(() => {
        this.setScrollListeners()
      })
    },

    /**
     * Update height data when override value changes.
     */
    heightOverride(newVal) {
      if (newVal) {
        this.oldHeight = this.$refs.scrollElement.clientHeight
        this.newHeight = this.oldHeight

        // setTimeout to change height at end of event queue.
        setTimeout(() => {
          this.newHeight = newVal
        }, 0)

        return
      }

      this.newHeight = this.oldHeight
    },
  },

  mounted() {
    this.setEventBusListeners()
    this.setAutoShow()

    if (this.customScroll) {
      this.setResizeObserver()
    }
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      clearFocusTrap: 'accessibility/clearFocusTrap',
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
      queueOverlay: 'overlays/queue',
      setFocusTrap: 'accessibility/setFocusTrap',
      setPreviousElement: 'accessibility/setPreviousElement',
    }),

    /**
     * Observe element resize.
     */
    setResizeObserver() {
      if (!this.$refs.body) {
        return
      }

      const resizeObserver = new ResizeObserver(() => {
        this.setOverflowState()
      })

      resizeObserver.observe(this.$refs.body)
    },

    /**
     * Set scroll event listeners.
     */
    setScrollListeners() {
      this.$refs.scrollElement.addEventListener('scroll', this.updateThumbPosition)
      this.$refs.scrollbarThumb.addEventListener('mousedown', this.handleThumbDrag)
    },

    /**
     * Update custom scroll thumb position
     * @param {Event} event
     */
    updateThumbPosition(event) {
      if (!this.customScrollActive) {
        return
      }

      const scrollableArea = this.totalHeight - this.visibleHeight
      const multiplier = event.target.scrollTop / scrollableArea

      const thumbHeightPx = this.$refs.scrollbarThumb.offsetHeight
      const offset = (this.visibleHeight - thumbHeightPx) * multiplier

      this.thumbPosition = `${offset}px`
    },

    /**
     * Scroll content on scrollbar Thumb drag.
     * @param {Event} event
     */
    handleThumbDrag(event) {
      if (!this.customScrollActive) {
        return
      }

      const config = {
        previous: 0,
        first: true,
        offset: 0,
        startY: event.clientY,
        scrollableArea: this.totalHeight - this.visibleHeight,
      }

      this.mouseMoveHandler = (ev) => this.onMouseMove(ev, config)
      this.mouseUpHandler = () => this.onMouseUp()

      window.addEventListener('mousemove', this.mouseMoveHandler)
      window.addEventListener('mouseup', this.mouseUpHandler)
    },

    /**
     * Update scroll position on mouse move
     * @param {Event} ev - mousemove event
     * @param {Object} config - default position data
     */
    onMouseMove(ev, config) {
      ev.preventDefault()

      const deltaY = ev.clientY - config.startY

      if (deltaY === config.previous) {
        return
      }

      config.previous = deltaY
      const multiplier = deltaY / this.visibleHeight
      const scrollTo = config.scrollableArea * multiplier

      if (config.first) {
        config.first = false
        config.offset = this.$refs.scrollElement.scrollTop
      }

      this.$refs.scrollElement.scrollTop = scrollTo + config.offset
    },

    /**
     * Remove event listeners on mouseup.
     */
    onMouseUp() {
      window.removeEventListener('mousemove', this.mouseMoveHandler)
      window.removeEventListener('mouseup', this.mouseUpHandler)
    },

    /**
     * Determine if scrollElement has overflow and requires scrolling
     */
    setOverflowState() {
      if (!this.isActive) {
        return
      }

      const computedStyles = window.getComputedStyle(this.$refs.scrollElement, null)
      const offsetTop = parseFloat(computedStyles.getPropertyValue('padding-top'))
      const offsetBottom = parseFloat(computedStyles.getPropertyValue('padding-bottom'))
      const innerHeight = this.$refs.scrollElement.clientHeight - offsetTop - offsetBottom

      if (!innerHeight || innerHeight <= 0) {
        this.hasOverflow = false
        return
      }

      this.totalHeight = this.$refs.body.clientHeight
      this.visibleHeight = innerHeight
      this.hasOverflow = this.totalHeight > this.visibleHeight

      this.setThumbHeight()
    },

    /**
     * Set the height of the scrollbar thumb relative to length of content.
     */
    setThumbHeight() {
      this.thumbHeight = (this.visibleHeight / this.totalHeight) * 100
    },

    /**
     * Get scroll element.
     */
    getScrollElement() {
      if (!this.$refs.scrollElement) {
        this.scrollElement = false
        return
      }

      this.scrollElement =
        this.$refs.scrollElement.querySelector('[js-overlay="scrollElement"]') ||
        this.$refs.scrollElement
    },

    /**
     * Set modal to autoshow.
     */
    setAutoShow() {
      if (!this.autoShow || !this.hydrated) {
        return
      }

      setTimeout(() => {
        if (this.autoQueue) {
          this.queueOverlay({ namespace: this.namespace })
          return
        }

        this.openOverlay({ namespace: this.namespace })
      }, this.autoShow)
    },

    /**
     * Set EventBus listeners.
     */
    setEventBusListeners() {
      cnvs.EventBus.listen('overlay:update', (namespace) => {
        if (!this.isActive || namespace !== this.namespace) {
          return
        }

        this.updateFocusTrap()
        this.updateBodyScroll()
      })
    },

    /**
     * Update focus trap using Vuex.
     */
    updateFocusTrap() {
      this.setFocusTrap({ container: this.$refs.overlay })

      /**
       * Have to use timeout to wait for animation to complete.
       * - Safari doesn't support `preventScroll`.
       */
      setTimeout(() => {
        if (!this.getFirstElement) {
          return
        }

        this.getFirstElement.focus({ preventScroll: true })
      }, this.$timing('normal'))
    },

    /**
     * Update body scroll element.
     */
    updateBodyScroll() {
      if (this.scrollElement) {
        enableBodyScroll(this.scrollElement)
      } else {
        clearAllBodyScrollLocks()
      }

      this.getScrollElement()

      if (cnvs.storybook && !this.scrollElement) {
        return
      }

      disableBodyScroll(this.scrollElement)
    },

    /**
     * Define body scroll options.
     * - Adds support for [js-body-scroll-lock-ignore] attribute which allows
     *   element to maintain scrolling when disableBodyScroll is enabled.
     * @returns {Object}
     */
    bodyScrollOptions() {
      return {
        // eslint-disable-next-line consistent-return
        allowTouchMove: (element) => {
          const ignoredElement = element.closest('[js-body-scroll-lock-ignore]')
          return ignoredElement
        },
      }
    },

    /**
     * Emit position data on state change.
     */
    handleStateChange(state) {
      if (!state) {
        this.$emit('state-change', null)
        return
      }

      const computedStyles = window.getComputedStyle(this.$refs.scrollElement, null)
      const rect = this.$refs.scrollElement.getBoundingClientRect()
      const paddingTop = parseFloat(computedStyles.getPropertyValue('padding-top'))
      const paddingBottom = parseFloat(computedStyles.getPropertyValue('padding-bottom'))

      const position = {
        bottom: rect.bottom,
        height: rect.height,
        left: rect.left,
        right: rect.right,
        top: rect.top,
        width: rect.width,
        x: rect.x,
        y: rect.y,
        paddingTop,
        paddingBottom,
      }

      this.$emit('state-change', position)
    },
  },
}
</script>
